import { Users } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover } from 'react-tiny-popover';
import { ContactCheckboxList } from '~/components/common/ContactCheckboxList';
import { Button } from '~/components/ui';
import { optionMatcher } from '~/components/ui/inputs/optionMatcher';
import type { OrganizationBuyersQuery } from '~/graphql/graphql';
import { contactLabel } from '~/utils/contact';

export type BuyListTeamMember = Pick<
  OrganizationBuyersQuery['users'][number],
  'id' | 'firstName' | 'lastName'
>;

type BuyListTeamFilterProps = {
  readonly buyers: BuyListTeamMember[];
  readonly isLoadingBuyers: boolean;
  readonly hasBuyersFailed: boolean;
  readonly isLoadingSavedTeam: boolean;
  readonly teamMemberIds: string[];
  readonly teamActive: boolean;
  readonly onTeamActiveChange: (active: boolean) => void;
  readonly onSaveTeam: (teamMemberIds: string[]) => void;
};

export function BuyListTeamFilter({
  buyers,
  isLoadingBuyers,
  hasBuyersFailed,
  isLoadingSavedTeam,
  teamMemberIds,
  teamActive,
  onTeamActiveChange,
  onSaveTeam,
}: BuyListTeamFilterProps) {
  const { t } = useTranslation('inventory');
  const [isEditing, setIsEditing] = useState(false);
  const [draftTeam, setDraftTeam] = useState<Set<string> | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const isTeamEditorReady =
    !isLoadingBuyers && !hasBuyersFailed && !isLoadingSavedTeam;

  const editedTeam = draftTeam ?? new Set(teamMemberIds);

  const openEditorWithSavedTeam = () => {
    setDraftTeam(null);
    setSearchQuery('');
    setIsEditing(true);
  };

  const closeEditorDiscardingDraft = () => setIsEditing(false);

  const hasTeam = teamMemberIds.length > 0;

  const toggleDraftMember = (buyerId: string) => {
    setDraftTeam((previousDraftTeam) => {
      const nextDraftTeam = new Set(previousDraftTeam ?? teamMemberIds);
      if (nextDraftTeam.has(buyerId)) {
        nextDraftTeam.delete(buyerId);
      } else {
        nextDraftTeam.add(buyerId);
      }
      return nextDraftTeam;
    });
  };

  const matchesSearchQuery = optionMatcher(searchQuery);
  const matchingBuyers = buyers.filter((buyer) =>
    matchesSearchQuery({ label: contactLabel(buyer) })
  );

  const membersStillInOrganization = () =>
    [...editedTeam].filter((buyerId) =>
      buyers.some((buyer) => buyer.id === buyerId)
    );

  const teamEditorUnavailableMessage = () => {
    if (isLoadingBuyers || isLoadingSavedTeam) {
      return t('inventory.buy_list.filter.team_loading');
    }
    if (hasBuyersFailed) return t('inventory.buy_list.filter.team_load_failed');
    return t('inventory.buy_list.filter.team_empty');
  };

  const saveTeamFromDraft = () => {
    if (!isTeamEditorReady) return;
    onSaveTeam(membersStillInOrganization());
    closeEditorDiscardingDraft();
  };

  return (
    <div className="flex items-center gap-4">
      <div
        className="flex rounded border border-neut-border overflow-hidden"
        data-testid="buy-list-team-toggle"
      >
        <button
          type="button"
          aria-pressed={!teamActive}
          onClick={() => onTeamActiveChange(false)}
          className={`px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
            teamActive
              ? 'bg-neut-0 text-neut-sec hover:bg-neut-50'
              : 'bg-prim text-white'
          }`}
        >
          {t('inventory.buy_list.filter.team_all_buyers')}
        </button>
        <button
          type="button"
          aria-pressed={teamActive}
          aria-disabled={!hasTeam}
          title={hasTeam ? undefined : t('inventory.buy_list.filter.team_hint')}
          data-testid="buy-list-team-toggle-my-team"
          onClick={() => {
            if (hasTeam) onTeamActiveChange(true);
          }}
          className={`px-3 py-2 text-sm font-medium border-l border-neut-border transition-colors ${
            hasTeam ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'
          } ${
            teamActive
              ? 'bg-prim text-white'
              : 'bg-neut-0 text-neut-sec hover:bg-neut-50'
          }`}
        >
          {t('inventory.buy_list.filter.team_my_team', {
            count: teamMemberIds.length,
          })}
        </button>
      </div>

      <Popover
        isOpen={isEditing}
        onClickOutside={closeEditorDiscardingDraft}
        positions={['bottom', 'top']}
        align="start"
        containerStyle={{ zIndex: '50' }}
        content={
          <div
            data-testid="buy-list-team-editor"
            className="mt-2 w-[280px] bg-neut-bg-alt rounded-lg shadow-xl border border-neut-border p-4"
          >
            <h3 className="font-semibold text-sm text-neut-prim mb-2">
              {t('inventory.buy_list.filter.team_edit_title')}
            </h3>
            {!isTeamEditorReady || buyers.length === 0 ? (
              <p className="text-sm text-neut-sec">
                {teamEditorUnavailableMessage()}
              </p>
            ) : (
              <>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder={t(
                    'inventory.buy_list.filter.team_search_placeholder'
                  )}
                  aria-label={t(
                    'inventory.buy_list.filter.team_search_placeholder'
                  )}
                  data-testid="buy-list-team-search"
                  className="w-full rounded border border-neut-border px-2 py-1 text-sm mb-2"
                />
                {matchingBuyers.length === 0 ? (
                  <p className="text-sm text-neut-sec">
                    {t('inventory.buy_list.filter.team_search_empty')}
                  </p>
                ) : (
                  <ul className="max-h-[240px] overflow-y-auto">
                    <ContactCheckboxList
                      contacts={matchingBuyers}
                      selectedContactIds={editedTeam}
                      onToggle={(member) => toggleDraftMember(member.id)}
                      checkboxIdPrefix="buy-list-team"
                    />
                  </ul>
                )}
              </>
            )}
            <div className="flex justify-end mt-3">
              <Button
                size="sm"
                width="auto"
                disabled={!isTeamEditorReady}
                data-testid="buy-list-team-save"
                onClick={saveTeamFromDraft}
              >
                {t('inventory.buy_list.filter.team_save')}
              </Button>
            </div>
          </div>
        }
      >
        <Button
          size="sm"
          width="auto"
          variant="secondary"
          aria-expanded={isEditing}
          data-testid="buy-list-team-edit-button"
          onClick={
            isEditing ? closeEditorDiscardingDraft : openEditorWithSavedTeam
          }
        >
          <Users size={16} className="mr-1" />
          {t('inventory.buy_list.filter.team_edit')}
        </Button>
      </Popover>
    </div>
  );
}
